import { Stack, Typography, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import IosShareIcon from '@mui/icons-material/IosShare';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LinkIcon from '@mui/icons-material/Link';
import React, { useState } from 'react';
import Modal from '../../shared/Modal';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { trackEvent } from '../../../app/Analytics';
import UserRole from '../../../shared/AuthRoles';
import { OwnerRole } from '../../enums/users';

export default function IndividualExperienceHero(experience: any) {
    const loggedInUser = UserRole();
    const [openModal, setOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [modalComponent, setModalComponent] = useState('');
    const navigate = useNavigate();
    const experienceDetails = experience?.experience;
    const ownerId = experienceDetails?.owner?.id;
    const canManage =
        loggedInUser?.company === ownerId &&
        loggedInUser?.role === OwnerRole.Admin;

    const editExperience = () => {
        navigate(`/experiences/edit/${experienceDetails.id}`);
    };

    const onClickOpenShareModal = () => {
        setOpen(true);
        setModalMessage('Share this experience');
        setModalComponent('Share');
        trackEvent(
            'Experiences',
            'Share Experience Clicked',
            'Share Experience Clicked',
        );
    };

    const onClickOpenCalendarModal = () => {
        setOpen(true);
        setModalMessage('Link this calendar to an external calendar');
        setModalComponent('Calendar');
        trackEvent(
            'Experiences',
            'Link Calendar Clicked',
            'Link Calendar Clicked',
        );
    };

    const onClickOpenQrModal = async () => {
        setOpen(true);
        setModalMessage('Vehicle QR Code');
        setModalComponent('QR');
        trackEvent(
            'Experiences',
            'Vehicle QR Code Clicked',
            'Vehicle QR Code Clicked',
        );
    };

    const handleCloseModal = () => setOpen(false);

    const fullAddress = `${experienceDetails?.streetAddress} ${experienceDetails?.suburb} ${experienceDetails?.postcode} ${experienceDetails?.state} ${experienceDetails?.countryCode}`;

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            sx={{
                width: '100%',
                textAlign: 'left',
                gap: 1,
                display: 'flex',
                direction: 'row',
                py: 1,
                alignItems: { xs: 'flex-start', sm: 'center' },
            }}
        >
            {experienceDetails?.addressVisible && (
                <Typography variant='body2'>
                    <MuiLink
                        href={`https://www.google.com/maps/place/${encodeURIComponent(
                            fullAddress,
                        )}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        underline='none'
                        sx={(theme) => ({
                            color: theme.palette.text.primary,
                            transition: 'color 0.2s ease',
                            '&:hover': {
                                cursor: 'pointer',
                                color: theme.palette.primary.main,
                            },
                        })}
                    >
                        {fullAddress}
                    </MuiLink>
                </Typography>
            )}

            <Stack
                direction='row'
                alignItems='center'
                sx={{
                    width: experienceDetails?.addressVisible ? 'auto' : '100%',
                    justifyContent: 'flex-end',
                    gap: '10px',
                }}
            >
                {canManage && (
                    <>
                        <CustomButton
                            sx={(theme: any) => ({
                                p: 0,
                                minWidth: 'auto',
                                color: theme.palette.text.primary,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    backgroundColor: 'white.main',
                                },
                            })}
                            onClick={editExperience}
                        >
                            <EditIcon sx={{ fontSize: '16px' }} />
                        </CustomButton>

                        <CustomButton
                            sx={(theme: any) => ({
                                p: 0,
                                minWidth: 'auto',
                                color: theme.palette.text.primary,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                    cursor: 'pointer',
                                    color: 'primary.main',
                                    backgroundColor: 'white.main',
                                },
                            })}
                            onClick={onClickOpenCalendarModal}
                        >
                            <LinkIcon sx={{ fontSize: '20px' }} />
                        </CustomButton>

                        {experienceDetails?.type === 'vehicle' && (
                            <CustomButton
                                sx={(theme: any) => ({
                                    p: 0,
                                    minWidth: 'auto',
                                    color: theme.palette.text.primary,
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        backgroundColor: 'white.main',
                                    },
                                })}
                                onClick={onClickOpenQrModal}
                                disableRipple
                            >
                                <QrCode2Icon sx={{ fontSize: '16px' }} />
                            </CustomButton>
                        )}
                    </>
                )}

                {experienceDetails?.isAvailable && (
                    <CustomButton
                        sx={{
                            p: 0,
                            minWidth: 'auto',
                            color: 'textAccent.main',
                            backgroundColor: 'transparent',
                            '&:hover': {
                                cursor: 'pointer',
                                color: 'primary.main',
                                backgroundColor: 'white.main',
                            },
                        }}
                        onClick={onClickOpenShareModal}
                        disableRipple
                    >
                        <IosShareIcon sx={{ fontSize: '20px' }} />
                    </CustomButton>
                )}
            </Stack>

            <Modal
                open={openModal}
                onClose={handleCloseModal}
                message={modalMessage}
                component={modalComponent}
                details={experienceDetails}
            />
        </Stack>
    );
}
